import type {
  IntakeChartEntrySection,
  IntakeDemographicField,
  SchedulingIntakeClinicalAnswers,
} from "@emr/contracts";
import { type ReactNode, useEffect, useId, useState } from "react";
import type { PatientProfile } from "../../api/clinicalWorkspace";
import { ProfileSectionBody } from "../patient-profile/ProfileSectionBody";
import { profileSection } from "../patient-profile/profileSections";
import { useSectionEditing } from "../patient-profile/useSectionEditing";
import {
  CHART_ENTRY_SECTION_LABELS,
  type IntakeDetailLine,
  type IntakeDetailReview,
  type IntakeEntryProposals,
  type IntakeReasonDecision,
  isDetailChoice,
  PREGNANCY_LABELS,
} from "./intakeReviewModel";
import type { IntakeReviewClient } from "./intakeReviewQueries";
import "../../patient-profile.css";

/*
 * The answer steps of Review intake (PA-6). Each choice here only says what the finish button
 * will copy from the stored questionnaire; the request names what to apply, never a value.
 * Corrections are made afterwards with the ordinary chart editors, rendered here inside
 * `.pp-section` so they look and behave as on the patient's profile (patient-profile.css is
 * imported for that reason).
 */

/** How many entries the chart's list shows (a removed allergy or history entry is not shown). */
function chartEntryCount(
  sectionKey: IntakeChartEntrySection,
  profile: PatientProfile | undefined,
): number | undefined {
  const items: readonly { readonly status?: string }[] | undefined = profile?.[sectionKey];
  if (items === undefined) return undefined;
  return sectionKey === "medications"
    ? items.length
    : items.filter((item) => item.status !== "inactive").length;
}

/**
 * A chart section with its own editor for corrections. Its list is shown in full, folded into one
 * "On the chart (n)" line (the health answers), or left out (the details, already compared).
 */
export function ChartSectionPanel({
  sectionKey,
  profile,
  loadError,
  client,
  siteId,
  patientId,
  readBody = "shown",
  editLabel,
  onDirtyChange,
}: {
  readonly sectionKey: "demographics" | IntakeChartEntrySection;
  readonly profile: PatientProfile | undefined;
  readonly loadError: boolean;
  readonly client: IntakeReviewClient;
  readonly siteId: string;
  readonly patientId: string;
  readonly readBody?: "shown" | "collapsed" | "hidden";
  readonly editLabel: string;
  readonly onDirtyChange: (key: string, dirty: boolean) => void;
}) {
  const editing = useSectionEditing(client, siteId, patientId, profile);
  const [listOpen, setListOpen] = useState(false);
  const section = profileSection(sectionKey);
  const isEditing = editing.editing === sectionKey;
  const dirty = isEditing && editing.dirty;
  useEffect(() => {
    onDirtyChange(sectionKey, dirty);
  }, [dirty, onDirtyChange, sectionKey]);
  useEffect(() => () => onDirtyChange(sectionKey, false), [onDirtyChange, sectionKey]);
  const editable =
    profile !== undefined && (sectionKey === "demographics" || profile[sectionKey] !== undefined);
  const body = (
    <ProfileSectionBody
      section={section}
      profile={profile}
      loadError={loadError}
      editing={editing}
    />
  );
  const edit =
    !isEditing && editable ? (
      <button
        type="button"
        className="schedule-btn ghost sm"
        onClick={() => editing.begin(sectionKey)}
      >
        {editLabel}
      </button>
    ) : null;
  const saved =
    editing.savedSection === sectionKey ? (
      <p className="pp-saved" role="status">
        Saved.
      </p>
    ) : null;
  if (isEditing) return <div className="pp-section intake-review-chart">{body}</div>;
  if (readBody === "collapsed" && sectionKey !== "demographics") {
    const count = chartEntryCount(sectionKey, profile);
    return (
      <div className="pp-section intake-review-chart collapsed">
        <div className="intake-review-chart-tools">
          <IntakeDisclosure
            label={count === undefined ? "On the chart" : `On the chart (${count})`}
            regionLabel={`${CHART_ENTRY_SECTION_LABELS[sectionKey]} on the chart`}
            open={listOpen}
            onOpenChange={setListOpen}
          >
            {body}
          </IntakeDisclosure>
          {edit}
        </div>
        {/* An open list says "Saved." itself. */}
        {listOpen ? null : saved}
      </div>
    );
  }
  return (
    <div className="pp-section intake-review-chart">
      {edit}
      {readBody === "shown" ? body : saved}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Details: what differs from the chart, then one quiet line for what matches
// ---------------------------------------------------------------------------

function Warnings({
  lines,
  label = "To check",
}: {
  readonly lines: readonly (string | undefined)[];
  readonly label?: string;
}) {
  const shown = lines.filter((line): line is string => line !== undefined && line !== "");
  if (shown.length === 0) return null;
  return (
    <ul className="intake-review-warnings" aria-label={label}>
      {shown.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  );
}

/** "Use patient's" / "Keep chart's" for one detail, each showing the value it keeps. */
function DetailChoice({
  label,
  answer,
  current,
  keepWord,
  use,
  disabled,
  warnings,
  onPick,
}: {
  readonly label: string;
  readonly answer: string;
  readonly current: string;
  readonly keepWord: string;
  readonly use: boolean;
  readonly disabled: boolean;
  readonly warnings: readonly (string | undefined)[];
  readonly onPick: (use: boolean) => void;
}) {
  const name = useId();
  const option = (checked: boolean, word: string, value: string, pick: boolean) => (
    <label className={`intake-review-option${checked ? " selected" : ""}`}>
      <input type="radio" name={name} checked={checked} onChange={() => onPick(pick)} />
      <span className="intake-review-option-word">{word}</span>{" "}
      <span className="intake-review-option-value">{value}</span>
    </label>
  );
  return (
    <fieldset className="intake-review-row" disabled={disabled}>
      <legend className="intake-review-row-label">{label}</legend>
      <div className="intake-review-options">
        {option(use, "Use patient's", answer || "—", true)}
        {option(!use, keepWord, current, false)}
      </div>
      <Warnings lines={warnings} />
    </fieldset>
  );
}

/** A detail shown for reading: the answer, what the chart (or booking) says, and why. */
function DetailReadOnly({
  label,
  answer,
  current,
  currentWord,
  decided,
  warnings,
}: {
  readonly label: string;
  readonly answer: string;
  /** Undefined when there is nothing to compare with. */
  readonly current?: string | undefined;
  readonly currentWord?: string | undefined;
  readonly decided?: string | undefined;
  readonly warnings: readonly (string | undefined)[];
}) {
  return (
    <fieldset className="intake-review-row readonly">
      <legend className="intake-review-row-label">{label}</legend>
      <dl className="intake-review-values">
        <div>
          <dt>Patient</dt>
          <dd>{answer || "—"}</dd>
        </div>
        {current === undefined ? null : (
          <div>
            <dt>{currentWord ?? "Chart"}</dt>
            <dd>{current}</dd>
          </div>
        )}
      </dl>
      {decided === undefined ? null : <p className="intake-review-decided">{decided}</p>}
      <Warnings lines={warnings} />
    </fieldset>
  );
}

function chartValue(line: IntakeDetailLine): string | undefined {
  if (line.chart === undefined) return undefined;
  if (line.same) return "Same";
  return line.chart || "Not on file";
}

/** A show/hide line: the count is the label, the list opens below it. */
export function IntakeDisclosure({
  label,
  regionLabel,
  className,
  open: controlledOpen,
  onOpenChange,
  children,
}: {
  readonly label: string;
  /** Names the opened list for assistive technology. */
  readonly regionLabel?: string | undefined;
  readonly className?: string | undefined;
  /** Held by the caller when it needs to know (else the line keeps its own state). */
  readonly open?: boolean | undefined;
  readonly onOpenChange?: ((open: boolean) => void) | undefined;
  readonly children: ReactNode;
}) {
  const [ownOpen, setOwnOpen] = useState(false);
  const open = controlledOpen ?? ownOpen;
  const setOpen = (update: (value: boolean) => boolean) => {
    const next = update(open);
    setOwnOpen(next);
    onOpenChange?.(next);
  };
  const bodyId = useId();
  return (
    <div className={["intake-review-disclosure", className].filter(Boolean).join(" ")}>
      <button
        type="button"
        className="intake-review-toggle"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((value) => !value)}
      >
        <span>{label}</span>
        <span className="intake-review-toggle-word">{open ? "Hide" : "Show"}</span>
      </button>
      <section
        id={bodyId}
        className="intake-review-disclosure-body"
        aria-label={regionLabel ?? label}
        hidden={!open}
      >
        {open ? children : null}
      </section>
    </div>
  );
}

/** The answers as a plain list. A card's follow-ups are shown above it, never only in here. */
function DetailList({ lines }: { readonly lines: readonly IntakeDetailLine[] }) {
  return (
    <dl className="intake-review-match-list">
      {lines.map((line) => (
        <div key={line.key}>
          <dt>{line.label}</dt>
          <dd>{line.answer || "—"}</dd>
        </div>
      ))}
    </dl>
  );
}

/** The reason for visit beside the booking's, as a row of the Details step. */
function ReasonRow({
  reason,
  decision,
  use,
  disabled,
  onPick,
}: {
  readonly reason: string;
  readonly decision: IntakeReasonDecision;
  readonly use: boolean;
  readonly disabled: boolean;
  readonly onPick: (use: boolean) => void;
}) {
  if (decision.kind === "hidden") return null;
  if (decision.kind === "choice") {
    return (
      <DetailChoice
        label="Reason for visit"
        answer={reason}
        current={decision.booking || "—"}
        keepWord="Keep booking's"
        use={use}
        disabled={disabled}
        warnings={[]}
        onPick={onPick}
      />
    );
  }
  return (
    <DetailReadOnly
      label="Reason for visit"
      answer={reason}
      current={decision.booking === undefined ? undefined : decision.booking || "—"}
      currentWord="Booking"
      decided={decision.decided}
      warnings={[decision.warning]}
    />
  );
}

/**
 * Step 2, Details: only what differs from the chart or needs a check, each with its choice, then
 * one line for every detail the chart already has. Before a chart is linked the answers can be
 * read, but nothing can be compared or chosen except the reason for visit.
 */
export function IntakeDetailsStep({
  details,
  identityAsked,
  linked,
  chartState,
  unlinkedNote,
  canChoose,
  picked,
  busy,
  onPick,
  reason,
  editor,
  onRetryChart,
}: {
  readonly details: IntakeDetailReview;
  /** The questionnaire asked who the patient is (a returning patient's asks only coverage). */
  readonly identityAsked: boolean;
  readonly linked: boolean;
  /** The linked chart's read: still loading, failed, or here. */
  readonly chartState: "loading" | "error" | "ready";
  readonly unlinkedNote: string;
  /** The review is open and the chart is read, so details can be chosen. */
  readonly canChoose: boolean;
  readonly picked: ReadonlySet<IntakeDemographicField>;
  readonly busy: boolean;
  readonly onPick: (field: IntakeDemographicField, use: boolean) => void;
  readonly reason: {
    readonly text: string;
    readonly decision: IntakeReasonDecision;
    readonly use: boolean;
    readonly onPick: (use: boolean) => void;
  };
  /** The chart's own details editor, when corrections can be made here. */
  readonly editor?: ReactNode;
  /** Reads the linked chart again after a failed read. */
  readonly onRetryChart: () => void;
}) {
  const reasonRow = (
    <ReasonRow
      reason={reason.text}
      decision={reason.decision}
      use={reason.use}
      disabled={busy}
      onPick={reason.onPick}
    />
  );
  const count = details.matching.length;
  // The health card's follow-ups sit on the coverage row once it is compared; until then they are
  // one visible line here, never only inside a folded list.
  const cardChecks =
    linked && chartState === "ready" ? null : (
      <Warnings
        lines={details.lines.find((line) => line.key === "coverage")?.warnings ?? []}
        label="Health card to check"
      />
    );
  return (
    <div className="intake-review-details">
      {identityAsked ? null : (
        <p className="intake-review-note">Returning patient: only coverage was asked.</p>
      )}
      {cardChecks}
      {!linked ? (
        <>
          {reasonRow}
          <p className="intake-review-note">{unlinkedNote}</p>
          {details.lines.length > 0 ? (
            <IntakeDisclosure
              label={`The patient's answers (${details.lines.length})`}
              regionLabel="The patient's answers"
            >
              <DetailList lines={details.lines} />
            </IntakeDisclosure>
          ) : null}
        </>
      ) : chartState === "loading" ? (
        <p className="intake-review-note" role="status">
          Loading the chart…
        </p>
      ) : chartState === "error" ? (
        <div className="schedule-modal-error" role="alert">
          <span>The chart couldn't be loaded.</span>
          <button type="button" className="schedule-btn ghost sm" onClick={onRetryChart}>
            Try again
          </button>
        </div>
      ) : (
        <>
          {details.attention.map((line) =>
            isDetailChoice(line) && canChoose ? (
              <DetailChoice
                key={line.key}
                label={line.label}
                answer={line.answer}
                current={line.chart || "Not on file"}
                keepWord="Keep chart's"
                use={picked.has(line.field)}
                disabled={busy}
                warnings={[line.caution, ...line.warnings]}
                onPick={(use) => onPick(line.field, use)}
              />
            ) : (
              <DetailReadOnly
                key={line.key}
                label={line.label}
                answer={line.answer}
                current={chartValue(line)}
                decided={line.decided}
                warnings={line.warnings}
              />
            ),
          )}
          {reasonRow}
          {count > 0 ? (
            <IntakeDisclosure
              className="intake-review-matching"
              label={
                count === 1 ? "1 detail matches the chart" : `${count} details match the chart`
              }
              regionLabel="Details that match the chart"
            >
              <DetailList lines={details.matching} />
            </IntakeDisclosure>
          ) : null}
        </>
      )}
      {editor}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Allergies, medications and medical history
// ---------------------------------------------------------------------------

const NONE_NOTES: Readonly<Record<IntakeChartEntrySection, string>> = {
  allergies: "Ask before recording no known allergies.",
  medications: "Nothing to add.",
  pmh: "Nothing to add.",
};

/**
 * One health answer: the patient's words, the parts that can be added word for word (ticked to
 * add, unticked to skip), the parts already on the chart, then the chart's list folded into one
 * line beside its own editor. Before a chart is linked the step says once why nothing can be added.
 */
export function IntakeEntriesPanel({
  section,
  proposals,
  picked,
  canApply,
  busy,
  profile,
  profileLoadError,
  patientId,
  siteId,
  client,
  onPick,
  onDirtyChange,
}: {
  readonly section: IntakeChartEntrySection;
  readonly proposals: IntakeEntryProposals;
  readonly picked: ReadonlySet<string>;
  /** The review is open and the linked chart's list is read. */
  readonly canApply: boolean;
  readonly busy: boolean;
  readonly profile: PatientProfile | undefined;
  readonly profileLoadError: boolean;
  /** The linked chart, or null before one is linked. */
  readonly patientId: string | null;
  readonly siteId: string;
  readonly client: IntakeReviewClient;
  readonly onPick: (display: string, add: boolean) => void;
  readonly onDirtyChange: (key: string, dirty: boolean) => void;
}) {
  const label = CHART_ENTRY_SECTION_LABELS[section];
  const headingId = useId();
  const addable = proposals.entries.filter((entry) => !entry.onChart);
  const onChart = proposals.entries.filter((entry) => entry.onChart);
  const written = proposals.answer.trim() !== "";
  return (
    <section className="intake-review-entry" aria-labelledby={headingId}>
      <h4 className="intake-review-entry-title" id={headingId}>
        {label}
      </h4>
      <p className={written ? "intake-review-answer" : "intake-review-answer quiet"}>
        {written ? proposals.answer : "Nothing written."}
      </p>
      {canApply && addable.length > 0 ? (
        <fieldset className="intake-review-entries" disabled={busy}>
          <legend>Add to the chart</legend>
          {addable.map((entry) => (
            <label key={entry.display} className="schedule-modal-check">
              <input
                type="checkbox"
                checked={picked.has(entry.display)}
                onChange={(event) => onPick(entry.display, event.target.checked)}
              />
              <span>{entry.display}</span>
            </label>
          ))}
        </fieldset>
      ) : null}
      {onChart.length > 0 ? (
        <p className="intake-review-note">
          Already on the chart:{" "}
          {onChart
            .map((entry) =>
              entry.chartStatus === undefined
                ? entry.display
                : `${entry.display} (${entry.chartStatus})`,
            )
            .join(", ")}
          .
        </p>
      ) : null}
      {proposals.tooLong.length > 0 ? (
        <p className="intake-review-note">
          {proposals.tooLong.length === 1 ? "1 part is" : `${proposals.tooLong.length} parts are`}{" "}
          too long for one entry: add {proposals.tooLong.length === 1 ? "it" : "them"} with the
          chart editor.
        </p>
      ) : null}
      {proposals.noneAnswers.length > 0 ? (
        <p className="intake-review-note">
          The patient answered “{proposals.noneAnswers.join("”, “")}”. {NONE_NOTES[section]}
        </p>
      ) : null}
      {patientId === null ? null : (
        <ChartSectionPanel
          sectionKey={section}
          profile={profile}
          loadError={profileLoadError}
          client={client}
          siteId={siteId}
          patientId={patientId}
          readBody="collapsed"
          editLabel={`Edit ${label.toLowerCase()} on the chart`}
          onDirtyChange={onDirtyChange}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Symptoms and pregnancy: shown, written nowhere
// ---------------------------------------------------------------------------

export function IntakeVisitContextPanel({
  answers,
}: {
  readonly answers: SchedulingIntakeClinicalAnswers;
}) {
  const headingId = useId();
  return (
    <section className="intake-review-entry" aria-labelledby={headingId}>
      <h4 className="intake-review-entry-title" id={headingId}>
        Symptoms and pregnancy
      </h4>
      <dl className="intake-review-facts">
        <div>
          <dt>Symptoms</dt>
          <dd>
            {answers.symptoms.length === 0 ? (
              "None chosen"
            ) : (
              <ul className="intake-review-symptoms">
                {answers.symptoms.map((symptom) => (
                  <li key={symptom}>{symptom}</li>
                ))}
              </ul>
            )}
          </dd>
        </div>
        <div>
          <dt>Pregnant</dt>
          <dd>{PREGNANCY_LABELS[answers.pregnant]}</dd>
        </div>
      </dl>
      <p className="intake-review-note">For reading only: not added to the chart.</p>
    </section>
  );
}
